import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Paper,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useAssetsApi } from 'src/modules/assetLibrary/api/AssetsApi';

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  maxHeight: '100%',
  overflowY: 'auto',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontSize: '0.75rem',
  padding: '4px 8px',
  borderRight: `1px solid ${theme.palette.divider}`,
  maxWidth: '150px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  '&:last-child': {
    borderRight: 'none',
  },
}));

const StyledHeaderCell = styled(StyledTableCell)(({ theme }) => ({
  backgroundColor: theme.palette.grey[50],
  color: theme.palette.text.primary,
  fontWeight: theme.typography.fontWeightBold,
  position: 'sticky',
  top: 0,
  zIndex: 1,
}));

interface CsvPreviewProps {
  csvUrl: string;
  maxRows?: number;
  width?: string;
  height?: string;
}

interface ParsedCsvData {
  headers: string[];
  rows: string[][];
}

const parseCsv = (csvText: string): ParsedCsvData => {
  const lines = csvText.trim().split('\n');
  if (lines.length === 0) {
    return { headers: [], rows: [] };
  }

  // Simple CSV parser - handles basic cases
  const parseRow = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++; // Skip next quote
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    
    result.push(current.trim());
    return result;
  };

  const headers = parseRow(lines[0]);
  const rows = lines.slice(1).map(parseRow);

  return { headers, rows };
};

export const CsvPreview: React.FC<CsvPreviewProps> = ({
  csvUrl,
  maxRows = 100,
  width = '100%',
  height = '100%',
}) => {
  const [csvData, setCsvData] = useState<ParsedCsvData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { getFileContent } = useAssetsApi();

  useEffect(() => {
    const fetchCsvData = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await getFileContent(csvUrl);
        
        // Convert response data to text
        let csvText: string;
        if (typeof response.data === 'string') {
          csvText = response.data;
        } else {
          // Handle ArrayBuffer or other binary data
          const blob = new Blob([response.data]);
          csvText = await blob.text();
        }

        const parsedData = parseCsv(csvText);
        
        // Limit rows for performance
        if (parsedData.rows.length > maxRows) {
          parsedData.rows = parsedData.rows.slice(0, maxRows);
        }

        setCsvData(parsedData);
      } catch (err) {
        console.error('Error fetching CSV:', err);
        setError(err instanceof Error ? err.message : 'Failed to load CSV');
      } finally {
        setLoading(false);
      }
    };

    fetchCsvData();
  }, [csvUrl, maxRows, getFileContent]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width,
          height,
          minHeight: '200px',
        }}
      >
        <CircularProgress size={24} />
        <Typography variant="body2" sx={{ ml: 2 }}>
          Loading CSV...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width,
          height,
          minHeight: '200px',
          flexDirection: 'column',
        }}
      >
        <Typography variant="body2" color="error" sx={{ mb: 1 }}>
          Failed to load CSV
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {error}
        </Typography>
      </Box>
    );
  }

  if (!csvData || csvData.headers.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width,
          height,
          minHeight: '200px',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          No CSV data found
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width, height, overflow: 'hidden' }}>
      <Paper elevation={0} sx={{ height: '100%', overflow: 'hidden' }}>
        <StyledTableContainer>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {csvData.headers.map((header, index) => (
                  <StyledHeaderCell key={index} title={header}>
                    {header || `Column ${index + 1}`}
                  </StyledHeaderCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {csvData.rows.map((row, rowIndex) => (
                <TableRow key={rowIndex} hover>
                  {csvData.headers.map((_, colIndex) => (
                    <StyledTableCell key={colIndex} title={row[colIndex] || ''}>
                      {row[colIndex] || ''}
                    </StyledTableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </StyledTableContainer>
        {csvData.rows.length >= maxRows && (
          <Typography variant="caption" color="text.secondary" sx={{ p: 1, display: 'block' }}>
            Showing first {maxRows} rows of {csvData.rows.length + 1} total
          </Typography>
        )}
      </Paper>
    </Box>
  );
};

export default CsvPreview;
